import { useNavigate } from 'react-router-dom'
import {
  Box,
  Modal,
  Typography,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material'
import { Check, CopyAll } from '@mui/icons-material'
import { useCopyToClipboard } from 'react-use'
import { CurrentScanDetails } from 'lib/scan-handler'
import { parseUdi } from 'lib/utils/codeIdentifierParser'

interface AlternateScanResultModalProps {
  currentScanValue?: string
  lookupQuery: CurrentScanDetails['lookupQuery']
  results: CurrentScanDetails['results']
  isComplete: CurrentScanDetails['isComplete']
  valueType: CurrentScanDetails['type']
  onClose: () => void
  show?: boolean
}

export function AlternateScanResultModal({
  show = false,
  currentScanValue,
  results,
  lookupQuery,
  isComplete,
  valueType,
  onClose,
}: AlternateScanResultModalProps) {
  const isSm = useMediaQuery((theme: any) => theme.breakpoints.up('sm'))
  const [isCopied, copyToClipboard] = useCopyToClipboard()
  const navigate = useNavigate()

  const noResultsFound = results?.length === 0 && lookupQuery.called
  const isURL = valueType === 'url'
  const nonUDI = valueType === 'unknown'
  const isUDI = valueType === 'udi'

  const isOpen = (isComplete && (noResultsFound || isURL)) || show

  // Parse UDI data for product request
  const parsedUdiData = currentScanValue ? parseUdi(currentScanValue) : null

  const handleCreateProductRequest = () => {
    const basePath = window.location.pathname.split('/create')[0]
    navigate(`${basePath}/create`, {
      state: {
        scannedCode: currentScanValue,
        deviceId: parsedUdiData?.deviceId || undefined,
        expirationDate: parsedUdiData?.expirationDate || undefined,
        lotBatch: parsedUdiData?.lotBatch || undefined,
        serialNumber: parsedUdiData?.serialNumber || undefined,
      },
    })
  }

  return (
    <Modal
      open={isOpen}
      BackdropProps={{ sx: { backgroundColor: 'rgba(40, 40, 40, 0.90)' } }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
      disableAutoFocus
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          maxWidth: 600,
          width: '100%',
          p: isSm ? 2 : 4,
        }}
      >
        <Box p={2}>
          <Typography
            variant="h3"
            sx={{
              position: 'relative',
              fontSize: 20,
            }}
          >
            Capture Value
            <Typography
              fontWeight={500}
              color="primary"
              maxWidth="60ch"
              sx={{ wordWrap: 'break-word' }}
            >
              {currentScanValue}
              <Tooltip title="Copy Capture Value">
                <IconButton
                  aria-label="copy"
                  size="small"
                  onClick={() => copyToClipboard(currentScanValue ?? '')}
                  sx={{ position: 'relative', left: 2, bottom: 2 }}
                >
                  {Boolean(isCopied.value) ? <Check /> : <CopyAll />}
                </IconButton>
              </Tooltip>
            </Typography>
          </Typography>
          <Box mt={1.5}>
            <Typography color="#333">
              Character Count:{' '}
              <Typography
                component="span"
                fontWeight={500}
                display="inline-block"
              >
                {currentScanValue?.length ?? 0}
              </Typography>
            </Typography>
            {isURL && <Typography color="#333">This code is a URL</Typography>}
            {isUDI && (
              <Typography color="#333">This code looks like a UDI</Typography>
            )}
            {nonUDI && (
              <Typography color="#333">This code is not a UDI</Typography>
            )}
            {noResultsFound && (
              <Typography mt={2} fontWeight={500}>
                This Product is not registered with the FDA or in the MedGEO
                database. Click to add to your documentation and create a
                product request.
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            mt={3}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 1.5 },
              '& > *': {
                width: { xs: '100%', sm: 'auto' },
              },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onClose}
            >
              Capture Again
            </Button>
            {isURL ? (
              <Button
                href={currentScanValue ?? ''}
                size="small"
                variant="contained"
                color="primary"
                target="_blank"
                onClick={() => navigate('../scan')}
              >
                Visit URL
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disableElevation
                  onClick={() => {
                    const basePath = window.location.pathname.split('/asset')[0]
                    navigate(`${basePath}/asset/manual`, {
                      state: { scannedCode: currentScanValue },
                    })
                  }}
                >
                  Search
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disableElevation={!isUDI}
                  onClick={handleCreateProductRequest}
                >
                  Create Product Request
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
